import React from 'react';
import { connect } from 'react-redux';
import { DARK_MODE, N0_DATA_FOUND } from '../../constants';
import { useResolutionScale } from '../../hooks';

const greenColor = '#49a47e';
const redColor = 'red';
const yellowColor = '#ffa222';

const PartScanningDetailsPopup = ({
	parts,
	attributes,
	themeMode,
	partErrorMessage,
	attributeErrorMessage,
}) => {
	const { scaleValue } = useResolutionScale();

	const containerStyle = {
		padding: `${scaleValue(20)}px ${scaleValue(20)}px ${scaleValue(30)}px ${scaleValue(20)}px`,
		width: 'auto',
		borderRadius: scaleValue(24),
		height: 'auto',
		display: 'flex',
		flexDirection: 'column',
		fontSize: scaleValue(14),
		maxWidth: scaleValue(900),
		overflowY: 'auto',
		background: themeMode === DARK_MODE
			? 'linear-gradient(115.48deg, #1A2127 31.9%, #151A1F 100%)'
			: 'linear-gradient(115.48deg, #DFDFDF 31.9%, #F1F1F1 99.99%)',
		border: themeMode === DARK_MODE ? '1px solid #404040' : '1px solid #B0B0B0',
		boxSizing: 'border-box',
	};

	const sectionContainer = {
		padding: `0 ${scaleValue(20)}px`,
		display: 'block',
		marginBottom: scaleValue(20),
	};

	const sectionHeader = {
		color: themeMode === DARK_MODE ? '#FFFFFF' : '#202020',
		borderBottom: themeMode === DARK_MODE ? '1px solid #404040' : '1px solid #B0B0B0',
		margin: 0,
		marginTop: scaleValue(5),
		marginBottom: scaleValue(10),
		paddingBottom: scaleValue(8),
		height: scaleValue(24),
		fontSize: scaleValue(16),
		fontWeight: 700,
		lineHeight: `${scaleValue(24)}px`,
	};

	const stationHeader = {
		color: '#E1D553',
		margin: 0,
		marginTop: scaleValue(15),
		marginBottom: scaleValue(8),
		fontSize: scaleValue(16),
		fontWeight: 600,
		lineHeight: `${scaleValue(20)}px`,
	};

	const partRow = {
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		color: themeMode === DARK_MODE ? 'white' : 'black',
		fontSize: scaleValue(14),
		marginBottom: scaleValue(8),
	};

	const strongLabel = {
		fontWeight: 800,
		fontSize: scaleValue(14),
	};

	const rightAlign = {
		textAlign: 'right',
		flex: 1,
	};

	const messageText = {
		color: themeMode === DARK_MODE ? 'white' : 'black',
		width: scaleValue(260),
		fontSize: scaleValue(14),
		marginTop: scaleValue(10),
	};

	return (
		<div style={containerStyle}>
			{/* === Part Families Section === */}
			<div style={sectionContainer}>
				<div style={sectionHeader}>Part Families</div>

				{(!parts?.stationGroups?.length || partErrorMessage) && (
					<div style={messageText}>
						{partErrorMessage || N0_DATA_FOUND}
					</div>
				)}

				{parts?.stationGroups?.map((station, stationIndex) => (
					<div key={`station-${stationIndex}`}>
						<div style={stationHeader}>{station.stationName}</div>
						{station.parts?.map((part, partIndex) => {
							let color;
							if (part.scannedValue && !part.skipReason) {
								color = greenColor;
							} else if (part.skipReason) {
								color = redColor;
							} else {
								color = yellowColor;
							}

							// Display name with index if quantity > 1
							const displayName = part.quantity > 1
								? `${part.familyName} (${part.index + 1})`
								: part.familyName;

							return (
								<div key={`part-${partIndex}`} style={partRow}>
									<strong style={strongLabel}>{displayName}</strong>
									<div style={{ ...rightAlign, color }}>
										{part.scannedValue || part.skipReason || 'Pending'}
									</div>
								</div>
							);
						})}
					</div>
				))}
			</div>

			{/* === Machine Operation Section === */}
			<div style={sectionContainer}>
				<div style={sectionHeader}>Machine Operation</div>

				{(!attributes?.stationGroups?.length || attributeErrorMessage) && (
					<div style={messageText}>
						{attributeErrorMessage || N0_DATA_FOUND}
					</div>
				)}

				{attributes?.stationGroups?.map((station, stationIndex) => (
					<div key={`machine-station-${stationIndex}`}>
						<div style={stationHeader}>{station.stationName}</div>
						{station.operations?.map((operation, opIndex) => {
							// Determine color based on status
							let color;
							if (operation.status === 'OK') {
								color = greenColor;
							} else if (operation.status === 'Skipped' || operation.status === 'NOK') {
								color = redColor;
							} else {
								color = yellowColor; // Pending
							}

							// Display name with batch number if quantity > 1
							const displayName = operation.quantity > 1
								? `${operation.attributeName} (${operation.batchNumber})`
								: operation.attributeName;

							return (
								<div key={`operation-${opIndex}`} style={partRow}>
									<strong style={strongLabel}>{displayName}</strong>
									<div style={{ ...rightAlign, color }}>
										{operation.scannedValue || 'Pending'}
									</div>
								</div>
							);
						})}
					</div>
				))}
			</div>
		</div>
	);
};

const mapStateToProps = (state) => ({
	themeMode: state.theme.themeMode,
});

export default connect(mapStateToProps)(PartScanningDetailsPopup);
